import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Panel } from "../../ds";
import { Switch } from "../ui/switch";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../ui/alert-dialog";

export const BrandCard = ({ brand, onChanged }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ready = brand.counts?.approved_waiting_n8n || 0;
  const published = brand.counts?.published || 0;
  const waiting = brand.counts?.waiting_visual_approval || 0;
  const failed = brand.counts?.failed || 0;

  const save = async (enabled) => {
    setBusy(true);
    try {
      await api.patch(`/social/brands/${brand.slug}/publishing`, { enabled });
      toast.success(`Publishing ${enabled ? "ON" : "OFF"} for ${brand.name}.`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  const onToggle = (next) => (next ? setConfirmOpen(true) : save(false));

  return (
    <Panel testId={`brand-card-${brand.slug}`} className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-[color:var(--muted)]">
            {brand.project_exists
              ? <Link to={`/projects/${brand.project_slug}`} className="hover:text-[color:var(--cobalt)] underline-offset-2 hover:underline" data-testid={`brand-project-link-${brand.slug}`}>{brand.project_slug}</Link>
              : <span data-testid={`brand-no-project-${brand.slug}`}>{brand.slug}</span>}
          </p>
          <h3 className="text-base font-semibold text-[color:var(--fg)]">{brand.name}</h3>
        </div>
        <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.08em]">
          <span
            style={{ color: brand.publish_enabled ? "#2EB8EF" : "var(--muted)" }}
            data-testid={`publishing-state-${brand.slug}`}
          >
            Publishing {brand.publish_enabled ? "ON" : "OFF"}
          </span>
          <Switch
            checked={brand.publish_enabled}
            disabled={busy}
            onCheckedChange={onToggle}
            data-testid={`publishing-switch-${brand.slug}`}
          />
        </label>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 font-mono text-xs text-[color:var(--muted)] tabular-nums">
        <span data-testid={`brand-concepts-${brand.slug}`}>{brand.counts?.waiting_for_video || 0} awaiting concept approval</span>
        <span data-testid={`brand-in-production-${brand.slug}`}>{(brand.counts?.production_approved || 0) + (brand.counts?.producing || 0) + (brand.counts?.production_failed || 0)} in production</span>
        <span data-testid={`brand-waiting-${brand.slug}`}>{waiting} waiting approval</span>
        <span data-testid={`brand-ready-${brand.slug}`}>{ready} ready</span>
        <span data-testid={`brand-published-${brand.slug}`}>{published} published</span>
        <span data-testid={`brand-archived-${brand.slug}`}>{brand.counts?.archived || 0} archived</span>
        <span
          style={{ color: brand.failed_open ? "var(--bad)" : undefined }}
          data-testid={`brand-failed-${brand.slug}`}
        >
          {failed} failed
        </span>
      </div>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="publishing-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Turn publishing ON for {brand.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              n8n will be able to read approved posts for this brand and publish them to the live
              social channels at their scheduled time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="publishing-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); save(true); }}
              disabled={busy}
              data-testid="publishing-confirm-on"
            >
              Turn ON
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
};
